import { type ReactNode, useState } from 'react';

import { ApiError } from '@/shared/api/errors';
import {
  clientFieldLabel,
  correctionReasonText,
  proferCodeSuffix,
  proferFailurePasses,
  proferFailureText,
} from '@/shared/clients/profer';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { Stat } from '@/shared/ui/Stat';
import { formatDate } from '@/shared/utils/format';
import { cn } from '@/shared/utils/cn';

import type { ClientSync, ClientSyncChange, ClientSyncDetail } from './types';
import {
  running,
  stuck,
  useProferSync,
  useProferSyncs,
  useRequestProferSync,
} from './useProferSyncs';

/**
 * Profer's customer list, from Clientes (contract v10.46, ADR 0041 of the back).
 *
 * One line over the list says when it last came and what it brought, with «Actualizar
 * desde Profer» for the morning a client was added there and cannot wait for the night,
 * and «Ver informe» for what changed. Only for whoever administers, and only with Profer
 * connected: the screen does not change for anybody else.
 *
 * The report leads with **what arrives wrong from Profer**, because that is the one thing
 * somebody has to act on, and it is acted on in Profer: fixed here, the next night brings
 * it back as it comes.
 */
const when = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** «3 altas · 1 cambio · 1 enlazado»: only what is not zero, «sin cambios» if nothing.
 * «Filas sin leer» counts the rows without a code too, which the report lists apart. */
function summary(sync: ClientSync): string {
  const parts = [
    sync.created ? plural(sync.created, 'alta', 'altas') : null,
    sync.changed ? plural(sync.changed, 'cambio', 'cambios') : null,
    sync.linked ? plural(sync.linked, 'enlazado', 'enlazados') : null,
    sync.corrected ? plural(sync.corrected, 'dato corregido', 'datos corregidos') : null,
    sync.rejected ? plural(sync.rejected, 'fila sin leer', 'filas sin leer') : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'sin cambios';
}

/** «3900» in quotes, and an empty value as the word, without quotes. */
function quoted(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  return text ? `«${text}»` : 'vacío';
}

function who(sync: ClientSync): string {
  if (sync.trigger === 'nightly') return 'la de cada noche';
  return sync.requested_by_name ? `pedida por ${sync.requested_by_name}` : 'pedida a mano';
}

export function ProferSync() {
  const syncs = useProferSyncs(true);
  const request = useRequestProferSync();
  const [open, setOpen] = useState<string | null>(null);
  // Said to a screen reader only once the person pressed the button: on arriving at the
  // screen, the line is there to be read like the rest.
  const [asked, setAsked] = useState(false);

  const latest = syncs.data?.items[0];
  const busy = running(latest) || request.isPending;
  // A 503 is the queue that did not take it (`CLI-07`): the back writes it as a failed
  // sync, and the line already says so. Twice, both as alerts, was once too many.
  const requestError =
    request.isError && !(request.error instanceof ApiError && request.error.status === 503);

  const button = (
    <div className="flex flex-wrap gap-2">
      {latest && latest.status !== 'running' ? (
        <Button variant="secondary" size="sm" onClick={() => setOpen(latest.id)}>
          Ver informe
        </Button>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => {
          setAsked(true);
          request.mutate();
        }}
      >
        {busy ? 'Actualizando…' : 'Actualizar desde Profer'}
      </Button>
    </div>
  );

  let line;
  let announcement = '';
  if (syncs.isPending) {
    line = <Spinner label="Cargando la última actualización desde Profer" />;
  } else if (!syncs.data) {
    // Could not be read: when it came is not known, and the error below says so.
    line = null;
  } else if (!latest) {
    line = (
      <Callout tone="neutral" compact title="Lista de Profer:" action={button}>
        todavía no se ha traído. Se trae sola cada noche.
      </Callout>
    );
  } else if (stuck(latest)) {
    // The worker stopped half way and nobody will finish it: pressing again closes it and
    // starts another (`start_sync` of the back).
    line = (
      <Callout
        tone="warn"
        compact
        title="La última actualización desde Profer no terminó:"
        action={button}
      >
        empezó el {formatDate(when, latest.started_at)} y lleva más de media hora sin acabar. Vuelve
        a pedirla.
      </Callout>
    );
  } else if (latest.status === 'running') {
    announcement = 'Actualizando desde Profer.';
    line = (
      <Callout tone="info" compact title="Actualizando desde Profer…" action={button}>
        Los clientes nuevos salen en la lista al terminar.
      </Callout>
    );
  } else if (latest.status === 'failed') {
    announcement = 'La actualización desde Profer ha fallado.';
    line = (
      <Callout
        tone="warn"
        compact
        title="La última actualización desde Profer falló:"
        action={button}
      >
        {formatDate(when, latest.started_at)}. {proferFailureText(latest.error_code)}
        {proferFailurePasses(latest.error_code) ? ' Puedes volver a probar en un rato.' : ''}
        {latest.error_code ? (
          <span className="text-ink-3">{proferCodeSuffix(latest.error_code)}</span>
        ) : null}
      </Callout>
    );
  } else {
    announcement = 'La actualización desde Profer ha terminado.';
    line = (
      <Callout tone="neutral" compact title="Lista de Profer:" action={button}>
        {formatDate(when, latest.finished_at ?? latest.started_at)}, {summary(latest)}.
      </Callout>
    );
  }

  return (
    <>
      {line}
      {/* Always mounted, only the text changes: a live region inserted whole is missed by
          screen readers, and the line itself holds buttons. */}
      <p className="sr-only" aria-live="polite">
        {asked ? announcement : ''}
      </p>
      {syncs.isError ? (
        <ErrorMessage error={syncs.error}>
          No se pudo leer cuándo vino la lista de Profer
        </ErrorMessage>
      ) : null}
      {requestError ? (
        <ErrorMessage error={request.error}>No se pudo pedir la actualización</ErrorMessage>
      ) : null}
      {open ? (
        <SyncReport
          id={open}
          others={syncs.data?.items ?? []}
          onPick={setOpen}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

function SyncReport({
  id,
  others,
  onPick,
  onClose,
}: {
  id: string;
  others: ClientSync[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const report = useProferSync(id);
  const sync = report.data;

  return (
    <Dialog
      title="Actualización desde Profer"
      size="lg"
      onClose={onClose}
      {...(sync
        ? {
            description: `${formatDate(when, sync.started_at)}, ${who(sync)}.`,
          }
        : {})}
    >
      {/* The sheet focuses this, not the first button: that is the list of other syncs at
          the bottom, and the sheet would open scrolled past what is wrong in Profer. */}
      <div data-autofocus tabIndex={-1} className="focus:outline-none">
        {report.isPending ? <Spinner label="Cargando el informe" /> : null}
        {report.isError ? (
          <ErrorMessage error={report.error}>No se pudo cargar el informe</ErrorMessage>
        ) : null}
        {sync ? <ReportBody sync={sync} /> : null}
      </div>

      {others.length > 1 ? (
        <section className="mt-6" aria-labelledby="profer-otras">
          <h3 id="profer-otras" className="text-ink text-[14px] font-semibold">
            Otras actualizaciones
          </h3>
          <ul className="divide-line-2 border-line mt-2 divide-y rounded-md border">
            {others.map((other) => (
              <li key={other.id}>
                <button
                  type="button"
                  aria-current={other.id === id ? 'true' : undefined}
                  onClick={() => onPick(other.id)}
                  className={cn(
                    'hover:bg-paper-2 flex w-full flex-wrap items-baseline justify-between gap-x-3 px-3 py-2 text-left text-[13.5px]',
                    other.id === id && 'bg-paper-2',
                  )}
                >
                  <span className="text-ink">
                    {formatDate(when, other.started_at)}
                    <span className="text-ink-3"> · {who(other)}</span>
                  </span>
                  <span className="text-ink-2">
                    {other.status === 'failed'
                      ? `falló${proferCodeSuffix(other.error_code)}`
                      : stuck(other)
                        ? 'no terminó'
                        : other.status === 'running'
                          ? 'en marcha'
                          : summary(other)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Dialog>
  );
}

function ReportBody({ sync }: { sync: ClientSyncDetail }) {
  if (sync.status === 'running') {
    return <Spinner label="Actualizando desde Profer" />;
  }
  if (sync.status === 'failed') {
    return (
      <Callout tone="warn" title="No se trajo nada">
        {proferFailureText(sync.error_code)} La lista de clientes sigue como estaba.
        {sync.error_code ? (
          <span className="text-ink-3">{proferCodeSuffix(sync.error_code)}</span>
        ) : null}
      </Callout>
    );
  }

  const changes = sync.changes ?? [];
  const byKind = (kind: ClientSyncChange['kind']) => changes.filter((c) => c.kind === kind);
  const created = byKind('alta');
  const linked = byKind('enlazado');
  const changed = byKind('cambio');
  const corrections = sync.corrections ?? [];
  const rejected = sync.rejected_rows ?? [];
  const ambiguous = sync.ambiguous ?? [];
  // Rows without a usable code: counted in «filas sin leer», and not in `rejected_rows`
  // because they were read fine and could not be kept (nothing to match them by).
  const withoutCode = sync.skipped ?? 0;
  const postalCodes = corrections.some((correction) => correction.field === 'postal_code');

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Llegaron" value={sync.received ?? 0} />
        <Stat label="Altas" value={sync.created ?? 0} />
        <Stat label="Cambios" value={sync.changed ?? 0} />
        <Stat label="Enlazados" value={sync.linked ?? 0} />
      </div>

      {corrections.length > 0 ? (
        <Section title="Datos que llegan mal de Profer">
          <p className="text-ink-2 text-[13.5px] leading-snug">
            Lo que se pudo arreglar se ha guardado arreglado, pero hay que corregirlo{' '}
            <strong>en Profer</strong>: si se cambia aquí, la noche siguiente lo vuelve a traer como
            venga.
            {postalCodes
              ? ' Mira bien los códigos postales: rellenar por la izquierda no siempre acierta (el 3900 de Torrelavega es 39300, no 03900).'
              : null}
          </p>
          <ul className="divide-line-2 border-line divide-y rounded-md border">
            {corrections.map((correction, index) => (
              <li
                key={`${correction.code ?? ''}-${correction.field}-${index}`}
                className="flex flex-col px-3 py-2 text-[13.5px]"
              >
                <span className="text-ink">
                  {correction.code ?? 'Sin código'} · {clientFieldLabel(correction.field)}
                </span>
                <span className="text-ink-2 [overflow-wrap:anywhere]">
                  {correction.field === 'code' && !correction.corrected
                    ? `Llegó ${quoted(correction.original)}: esa fila no se ha guardado`
                    : `Llegó ${quoted(correction.original)}, se guardó ${quoted(correction.corrected)}`}
                </span>
                {correctionReasonText(correction.reason) ? (
                  <span className="text-ink-3 text-[12.5px]">
                    {correctionReasonText(correction.reason)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {ambiguous.length > 0 ? (
        <Section title="Sin enlazar">
          <p className="text-ink-2 text-[13.5px] leading-snug">
            Estos NIF están en Profer y en algún cliente escrito aquí a mano, pero no uno a uno (más
            de una ficha en Profer, más de un cliente aquí, o la ficha de Profer ya estaba en
            Clientes), así que no se ha enlazado ninguno. Las fichas de Profer están en Clientes por
            su cuenta y lo escrito aquí sigue igual: en los presupuestos nuevos elige la de Profer y
            da de baja la otra.
          </p>
          <p className="text-ink text-[13.5px]">{ambiguous.join(' · ')}</p>
        </Section>
      ) : null}

      {rejected.length > 0 || withoutCode > 0 ? (
        <Section title="Filas que no se pudieron leer">
          <ul className="text-ink-2 text-[13.5px]">
            {rejected.map((row) => (
              <li key={row.line}>
                Fila {row.line} de la lista: {row.reason.replace(/^linea \d+:\s*/, '')}
              </li>
            ))}
            {withoutCode > 0 ? (
              <li>
                {withoutCode === 1
                  ? '1 fila sin código de cliente que se pueda usar: sin él no se puede guardar.'
                  : `${withoutCode} filas sin código de cliente que se pueda usar: sin él no se pueden guardar.`}
              </li>
            ) : null}
          </ul>
        </Section>
      ) : null}

      {linked.length > 0 ? (
        <Section title="Enlazados con su ficha de Profer">
          <p className="text-ink-2 text-[13.5px] leading-snug">
            Estaban dados de alta a mano. Ahora son los de Profer y siguen con sus presupuestos.
            Debajo de cada uno, lo que Profer cambió de lo escrito aquí; lo que Profer no tiene se
            ha quedado como estaba.
          </p>
          <ChangeList changes={linked} withFields />
        </Section>
      ) : null}

      {created.length > 0 ? (
        <Section title="Clientes nuevos">
          <ChangeList changes={created} />
        </Section>
      ) : null}

      {changed.length > 0 ? (
        <Section title="Cambiados">
          <ChangeList changes={changed} withFields />
        </Section>
      ) : null}

      {sync.truncated ? (
        <p className="text-ink-3 text-[12.5px]">
          Las listas muestran las 200 primeras de cada tipo; las cifras de arriba son todas.
        </p>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <h3 className="text-ink text-[14px] font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function ChangeList({
  changes,
  withFields = false,
}: {
  changes: ClientSyncChange[];
  withFields?: boolean;
}) {
  return (
    <ul className="divide-line-2 border-line divide-y rounded-md border">
      {changes.map((change) => (
        <li key={`${change.kind}-${change.code}`} className="flex flex-col px-3 py-2 text-[13.5px]">
          <span className="text-ink">
            <span className="text-ink-3 tabular-nums">{change.code}</span> {change.name}
          </span>
          {withFields && (change.fields ?? []).length > 0 ? (
            <span className="text-ink-2">
              {(change.fields ?? []).map(clientFieldLabel).join(', ')}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
